/**
 * Version utility for Neuro-HA UI.
 *
 * Reads version from package.json at build time.
 * Can also fetch the latest GitHub release version.
 */

// Import version from package.json (Vite handles this at build time)
import packageJson from '../../package.json'

export const APP_VERSION = packageJson.version

/**
 * Format version for display.
 * @param version - Version string (e.g., "0.1.0")
 * @returns Formatted version (e.g., "v0.1.0")
 */
export function formatVersion(version: string): string {
  return version.startsWith('v') ? version : `v${version}`
}

/**
 * Fetch latest release version from GitHub API.
 * Falls back to package.json version on error.
 *
 * @param owner - GitHub repo owner
 * @param repo - GitHub repo name
 * @returns Latest version string
 */
export async function fetchLatestGitHubVersion(
  owner: string = 'TroyFortinAnthr',
  repo: string = 'neuro-ha-ecosystem'
): Promise<string> {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${owner}/${repo}/releases/latest`,
      {
        headers: {
          Accept: 'application/vnd.github.v3+json',
        },
      }
    )

    if (!response.ok) {
      // No releases yet, fall back to package.json
      return APP_VERSION
    }

    const data = await response.json()
    return data.tag_name || APP_VERSION
  } catch {
    // Network error or API issue, fall back to package.json
    return APP_VERSION
  }
}

/**
 * Get version info including build metadata.
 */
export function getVersionInfo() {
  return {
    version: APP_VERSION,
    formattedVersion: formatVersion(APP_VERSION),
    buildTime: import.meta.env.VITE_BUILD_TIME || null,
    commitSha: import.meta.env.VITE_COMMIT_SHA || null,
  }
}

export default APP_VERSION
